import { CircularProgress, makeStyles, Theme } from '@freighthub/silhouette-core'
import { useObserver } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'

import {
  isCompanyUserActivity,
  isDocumentUploadActivity,
  isManualQuoteActivity,
  isMessageActivity,
  isProductUpdateActivity,
  isShipmentUpdateActivity,
} from '../activities'
import { TBaseActivity } from '../activities/BaseActivity'
import { TActivityFeed } from '../ActivityFeed'
import { DateSeparator } from '../DateSeparator'
import { TActivityFiltersModel } from '../filters/ActivityFilterModel'
import { ActivityFilters } from '../filters/ActivityFilters'
import { ChatMessage } from './ChatMessage'
import { LoadMore } from './LoadMore'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    position: 'relative',
    maxWidth: '660px',
    margin: theme.spacing(0, 'auto', 12.5),
  },
  reloadingProgress: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(5),
  },
}))

const getUserId = (activity: TBaseActivity): string | undefined => {
  if (
    isMessageActivity(activity) ||
    isDocumentUploadActivity(activity) ||
    isManualQuoteActivity(activity) ||
    isShipmentUpdateActivity(activity) ||
    isProductUpdateActivity(activity)
  ) {
    return activity.actor ? activity.actor._id : undefined
  }
  if (isCompanyUserActivity(activity)) {
    return activity.authorActor ? activity.authorActor._id : undefined
  }
}

type MessageContainerProps = {
  activityFeed: TActivityFeed
  activityFilter: TActivityFiltersModel
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  handleDownload: (params: { [key: string]: any }) => void
  hideFilters?: boolean
  loadMore: () => void
  isLoading: boolean
  showLoadMore: boolean
  displayFiltersLoader: boolean
  onFilterChange: () => void
}

export const MessagesContainer: FC<MessageContainerProps> = ({
  activityFeed,
  activityFilter,
  handleDownload,
  hideFilters,
  loadMore,
  isLoading,
  showLoadMore,
  displayFiltersLoader,
  onFilterChange,
}) => {
  const classes = useStyles()
  const renderDateSeparator = (currDate: Date, prevDate?: Date): ReactNode | null => {
    // Always render a date before the first activity
    if (!prevDate) {
      return <DateSeparator date={currDate} />
    }

    // render a separator for every new day
    const _prevDate = new Date(prevDate)
    _prevDate.setHours(0, 0, 0, 0)
    return currDate < _prevDate ? <DateSeparator date={currDate} hasPrevActivity /> : null
  }

  return useObserver(() => (
    <div className={classes.container}>
      {!hideFilters && <ActivityFilters filterModel={activityFilter} onFilterChange={onFilterChange} />}
      <div className={classes.reloadingProgress}>{displayFiltersLoader && <CircularProgress />}</div>
      {activityFilter
        .filter(activityFeed.activities)
        .map((activity: TBaseActivity, index: number, filteredActivities: TBaseActivity[]) => {
          const prevDate = index !== 0 ? filteredActivities[index - 1].updatedAt : null
          const isLast = filteredActivities[index + 1] == null
          return (
            <div key={activity._id}>
              {renderDateSeparator(activity.updatedAt, prevDate!)}
              <ChatMessage
                handleDownload={handleDownload}
                activity={activity}
                isLast={isLast}
                userId={getUserId(activity)}
              />
            </div>
          )
        })}
      {showLoadMore && <LoadMore loadMore={loadMore} isLoading={isLoading} />}
    </div>
  ))
}
